// Dati statici e helper per lo screening visivo dei videoterminalisti
import { loadDefaults } from "./defaults";

export const SNELLEN_LINES = [
  { label: "1/10", value: 1, letters: ["E"], size: 92 },
  { label: "2/10", value: 2, letters: ["F", "P"], size: 72 },
  { label: "3/10", value: 3, letters: ["T", "O", "Z"], size: 56 },
  { label: "4/10", value: 4, letters: ["L", "P", "E", "D"], size: 45 },
  { label: "5/10", value: 5, letters: ["P", "E", "C", "F", "D"], size: 37 },
  { label: "6/10", value: 6, letters: ["E", "D", "F", "C", "Z", "P"], size: 31 },
  { label: "7/10", value: 7, letters: ["F", "E", "L", "O", "P", "Z", "D"], size: 26 },
  { label: "8/10", value: 8, letters: ["D", "E", "F", "P", "O", "T", "E", "C"], size: 22 },
  { label: "9/10", value: 9, letters: ["L", "E", "F", "O", "D", "P", "C", "T"], size: 19 },
  { label: "10/10", value: 10, letters: ["P", "E", "Z", "O", "L", "C", "F", "T", "D"], size: 16 },
];

export const ACUITY_OPTIONS = [
  "10/10", "9/10", "8/10", "7/10", "6/10", "5/10", "4/10", "3/10", "2/10", "1/10", "< 1/10", "Non valutabile",
];

export const CONTRAST_ROWS = [
  { label: "100%", opacity: 1.0, letters: ["C", "H", "N"] },
  { label: "50%", opacity: 0.5, letters: ["O", "R", "V"] },
  { label: "25%", opacity: 0.25, letters: ["S", "K", "D"] },
  { label: "12%", opacity: 0.12, letters: ["Z", "N", "H"] },
  { label: "6%", opacity: 0.06, letters: ["R", "C", "O"] },
  { label: "3%", opacity: 0.03, letters: ["V", "S", "K"] },
];

export const CONTRAST_OPTIONS = ["3%", "6%", "12%", "25%", "50%", "100%", "Nessuna riga letta"];

export const NEAR_LINES = [
  { label: "J7", size: 30, text: "Sorveglianza sanitaria" },
  { label: "J5", size: 24, text: "Protezione della vista e degli occhi" },
  { label: "J3", size: 18, text: "Uso quotidiano del videoterminale in ufficio" },
  { label: "J2", size: 14, text: "Verifica della capacità visiva prossimale a circa 40 cm" },
  { label: "J1", size: 11, text: "Lettura di caratteri di piccole dimensioni ravvicinati sullo schermo" },
];

export const NEAR_OPTIONS = ["J1", "J2", "J3", "J5", "J7", "Non valutabile"];

export const ISHIHARA_PLATES = [
  { id: "p1", number: "12", img: "/ishihara/plate-12.jpg", zoom: 1.28 },
  { id: "p2", number: "74", img: "/ishihara/plate-74.jpg" },
  { id: "p3", number: "6", img: "/ishihara/plate-6.jpg", zoom: 1.16 },
  { id: "p4", number: "42", img: "/ishihara/plate-42.jpg", zoom: 1.27 },
  { id: "p5", number: "8", img: "/ishihara/plate-8.jpg" },
  { id: "p6", number: "29", img: "/ishihara/plate-29.jpg" },
  { id: "p7", number: "3", img: "/ishihara/plate-3.jpg" },
];

export const SYMPTOMS = [
  { key: "affaticamento", label: "Affaticamento visivo (astenopia)" },
  { key: "bruciore", label: "Bruciore o irritazione oculare" },
  { key: "secchezza", label: "Sensazione di occhio secco" },
  { key: "lacrimazione", label: "Lacrimazione eccessiva" },
  { key: "visioneOffuscata", label: "Visione offuscata o annebbiata" },
  { key: "malDiTesta", label: "Mal di testa / cefalea" },
  { key: "diplopia", label: "Visione doppia (diplopia)" },
  { key: "fotofobia", label: "Fastidio alla luce (fotofobia)" },
  { key: "difficoltaFuoco", label: "Difficoltà di messa a fuoco lontano-vicino" },
  { key: "doloreCollo", label: "Dolore a collo/spalle durante l'uso del VDT" },
];

export const FREQ_OPTIONS = ["Mai / raramente", "Talvolta", "Spesso", "Sempre"];

// Vocabolario a tre esiti condiviso da Amsler e dial astigmatico. Tenerlo unico non è solo
// deduplicazione: i tre valori esistono già in ogni blocco CLINICAL di i18n.js, e la parità di
// CLINICAL non è coperta da alcun test — un quarto valore introdotto qui resterebbe in italiano
// dentro un referto inglese senza che niente lo segnali.
const ESITI_3 = ["Normale", "Alterata", "Non valutabile"];

export const AMSLER_ESITI = ESITI_3;

export const AMSLER_ANOMALIE = [
  { key: "onde", label: "Linee ondulate o distorte" },
  { key: "scotoma", label: "Aree mancanti o scure (scotoma)" },
  { key: "sfocato", label: "Zone sfocate o annebbiate" },
  { key: "interrotte", label: "Linee interrotte" },
];

export const ASTIG_ESITI = ESITI_3;

export const REDGREEN_OPTIONS = [  { value: "rosso", label: "Più nitido sullo sfondo ROSSO" },
  { value: "verde", label: "Più nitido sullo sfondo VERDE" },
  { value: "uguale", label: "Uguale su entrambi gli sfondi" },
];

export const CONCLUSION_OPTIONS = [
  "Screening nella norma",
  "Consigliato approfondimento oculistico",
  "Non conclusivo / da ripetere",
];

export function initialData() {
  const today = new Date().toISOString().slice(0, 10);
  const d = loadDefaults();
  return {
    patient: {
      cognome: "", nome: "", dataNascita: "", genere: "",
      mansione: "", reparto: "", medico: d.medico || "", dataEsame: today,
      correzione: "", logo: d.logo || null, firma: d.firma || null,
      strutturaNome: d.strutturaNome || "", strutturaIndirizzo: d.strutturaIndirizzo || "",
    },
    symptoms: { flags: {}, frequenza: "", note: "" },
    snellen: { od: "", os: "", ou: "", note: "", distanceCm: 250 },
    contrast: { od: "", os: "", note: "" },
    near: { od: "", os: "", note: "" },
    amsler: { od: { esito: "", anomalie: {} }, os: { esito: "", anomalie: {} }, note: "" },
    // Tre condizioni di osservazione e non due: la tavola si guarda con l'occhio destro, con il
    // sinistro e infine con entrambi, come prescrive la procedura di riferimento. Nessuna
    // distanza registrata — la tavola non è dimensionata per una distanza (vedi
    // `astigmatismChartPx`).
    astigmatism: { od: { esito: "" }, os: { esito: "" }, ou: { esito: "" }, note: "" },
    ishihara: { responses: {}, note: "" },
    redGreen: { result: "", note: "" },
    conclusion: { esito: "", raccomandazioni: "" },
    calibration: { pxPerMm: d.calibrationPxPerMm || null },
  };
}

export function ishiharaScore(data) {
  let correct = 0;
  ISHIHARA_PLATES.forEach((p) => {
    if ((data.ishihara.responses[p.id] || "").trim() === "yes") correct += 1;
  });
  return { correct, total: ISHIHARA_PLATES.length };
}

function acuityBelow(v, threshold) {
  if (!v) return false;
  const m = v.match(/^(\d+)\/10$/);
  if (m) return parseInt(m[1], 10) < threshold;
  if (v.startsWith("<") || v === "Non valutabile") return true;
  return false;
}

export function autoOutcome(data) {
  const flags = [];
  if (acuityBelow(data.snellen.od, 7) || acuityBelow(data.snellen.os, 7)) {
    flags.push("Acuità visiva da lontano ridotta (< 7/10)");
  }
  if (["J5", "J7", "Non valutabile"].includes(data.near.od) ||
      ["J5", "J7", "Non valutabile"].includes(data.near.os)) {
    flags.push("Acuità visiva da vicino ridotta");
  }
  if (["100%", "50%", "Nessuna riga letta"].includes(data.contrast.od) ||
      ["100%", "50%", "Nessuna riga letta"].includes(data.contrast.os)) {
    flags.push("Ridotta sensibilità al contrasto");
  }
  const { correct, total } = ishiharaScore(data);
  const firstOk = (data.ishihara.responses["p1"] || "").trim() === "12";
  if (Object.keys(data.ishihara.responses).length > 0 && correct < total) {
    flags.push(firstOk
      ? "Possibile alterazione della visione dei colori (Ishihara)"
      : "Test cromatico non conclusivo (prima piastra non riconosciuta)");
  }
  if (data.amsler && (data.amsler.od.esito === "Alterata" || data.amsler.os.esito === "Alterata")) {
    flags.push("Possibile alterazione maculare (griglia di Amsler)");
  }
  const symCount = Object.values(data.symptoms.flags || {}).filter(Boolean).length;  if (symCount >= 3 || ["Spesso", "Sempre"].includes(data.symptoms.frequenza)) {
    flags.push("Sintomatologia astenopica rilevante");
  }
  return {
    level: flags.length === 0 ? "ok" : "warn",
    flags,
    suggestion: flags.length === 0
      ? "Screening nella norma"
      : "Consigliato approfondimento oculistico",
  };
}

export function formatDateIT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("it-IT");
  } catch {
    return iso;
  }
}

const ARCMIN_TO_RAD = Math.PI / (180 * 60);

// Dimensione (font-size px) di un ottotipo di Snellen per una data acuità decimale
// e distanza, usando lo schermo calibrato (px per mm).
export function snellenFontPx(vDecimal, pxPerMm, distanceMm = 500) {
  const capRatio = 0.72; // rapporto altezza maiuscola / font-size (Arial)
  const heightMm = distanceMm * (5 / vDecimal) * ARCMIN_TO_RAD;
  return (heightMm * pxPerMm) / capRatio;
}

// Proporzioni della tavola dell'astigmatismo: quattro cerchi riempiti ciascuno da un reticolo di
// linee parallele, orientate a 0°, 90°, 45° e 135°.
//
// La tavola NON è dimensionata per la distanza di osservazione, ed è una scelta, non una
// dimenticanza. Il segno che si cerca non è la soglia di risoluzione di un dettaglio — quella sì
// dipenderebbe dalla dimensione angolare — ma il *confronto fra* i quattro riquadri: se un
// meridiano appare sfocato mentre gli altri restano netti, lo è a qualunque scala, perché lo
// sfocamento astigmatico è direzionale, non dimensionale. Renderla alla dimensione angolare
// "giusta" non aggiungerebbe informazione e legherebbe il test a una calibrazione dello schermo
// che qui non serve. Non reintrodurre un cursore di distanza.
//
// I rapporti replicano la tavola di riferimento (cerchio da 150 px, passo 10, linea 1, bordo 2)
// normalizzati al diametro: la tavola resta quindi identica a sé stessa a qualunque dimensione
// venga resa, e il passo del reticolo non può scollarsi dal diametro.
export const ASTIG_CIRCLE_PX = 220; // diametro nominale di un cerchio
export const ASTIG_GAP_RATIO = 13 / 150; // spazio fra i cerchi
export const ASTIG_PITCH_RATIO = 10 / 150; // passo del reticolo (linea + spazio)
export const ASTIG_LINE_RATIO = 1 / 150; // spessore di una linea del reticolo
export const ASTIG_RING_RATIO = 2 / 150; // spessore del bordo del cerchio

// Orientamento del reticolo in ogni cerchio, in gradi e in ordine di lettura: alto-sinistra,
// alto-destra, basso-sinistra, basso-destra. Sono i quattro meridiani principali — i due
// cardinali più le due oblique — cioè le direzioni in cui cade la gran parte degli astigmatismi.
export const ASTIG_AXES = [0, 90, 45, 135];

// Geometria (px) della tavola per un dato diametro di cerchio.
export function astigmatismChartPx(circlePx = ASTIG_CIRCLE_PX) {
  const gap = circlePx * ASTIG_GAP_RATIO;
  return {
    circle: circlePx,
    gap,
    pitch: circlePx * ASTIG_PITCH_RATIO,
    line: circlePx * ASTIG_LINE_RATIO,
    ring: circlePx * ASTIG_RING_RATIO,
    size: circlePx * 2 + gap, // lato della tavola completa (2×2 cerchi)
  };
}
